import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { Tabs } from 'antd';
import SpecDrawer from '../../SpecDrawer/SpecDrawer';
import { ShowListing } from '../../DirectoryListings/ShowListing/ShowListing';
import { DateSubheader } from '../../DateSubheader';
import type { PillColor } from '../../DateSubheader';
import { XIcon } from '../../icons';
import type { ShowGroup, ShowWithId, ShowWithRawDate } from './UpcomingShowsSection';
import '../Profile.css';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);

export interface AllShowsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Flat list of shows (used when no groups) */
  shows?: ShowWithId[];
  /** Grouped shows (takes precedence over flat list) */
  showGroups?: ShowGroup[];
  /** All shows with rawDate for proper date filtering */
  allShows?: ShowWithRawDate[];
}

function getDatePillInfo(dateLabel: string): { label?: string; color: PillColor } {
  const cleaned = dateLabel.replace(/^[A-Za-z]+\s+/, '');
  const parsed = dayjs(cleaned, 'MMM D');
  if (!parsed.isValid()) return { color: 'grey' };

  const today = dayjs().startOf('day');
  const target = parsed.startOf('day');
  const diffDays = target.diff(today, 'day');

  if (diffDays === 0) return { label: 'Today', color: 'red' };
  if (diffDays === 1) return { label: 'Tomorrow', color: 'grey' };
  if (diffDays === -1) return { label: 'Yesterday', color: 'grey' };

  const todayDow = today.day();
  const mondayOffset = todayDow === 0 ? -6 : 1 - todayDow;
  const thisMonday = today.add(mondayOffset, 'day');
  const nextMonday = thisMonday.add(7, 'day');
  const lastMonday = thisMonday.subtract(7, 'day');

  const targetDow = target.day();
  const isWeekend = targetDow === 0 || targetDow === 6;

  if (diffDays <= -2 && !target.isBefore(thisMonday)) {
    return { label: `${Math.abs(diffDays)} Days Ago`, color: 'grey' };
  }

  if (!target.isBefore(thisMonday) && target.isBefore(nextMonday)) {
    if (isWeekend && diffDays > 0) return { label: 'This Weekend', color: 'grey' };
    if (diffDays > 0) return { label: 'This Week', color: 'grey' };
  }

  if (!target.isBefore(nextMonday) && target.isBefore(nextMonday.add(7, 'day'))) {
    return { label: isWeekend ? 'Next Weekend' : 'Next Week', color: 'grey' };
  }

  if (!target.isBefore(nextMonday.add(7, 'day')) && target.isBefore(nextMonday.add(14, 'day'))) {
    return { label: '2 Weeks', color: 'grey' };
  }

  if (!target.isBefore(nextMonday.add(14, 'day')) && target.isBefore(nextMonday.add(21, 'day'))) {
    return { label: '3 Weeks', color: 'grey' };
  }

  if (!target.isBefore(lastMonday) && target.isBefore(thisMonday)) {
    return { label: isWeekend ? 'Last Weekend' : 'Last Week', color: 'grey' };
  }

  if (!target.isBefore(lastMonday.subtract(7, 'day')) && target.isBefore(lastMonday)) {
    return { label: '2 Weeks Ago', color: 'grey' };
  }

  if (!target.isBefore(lastMonday.subtract(14, 'day')) && target.isBefore(lastMonday.subtract(7, 'day'))) {
    return { label: '3 Weeks Ago', color: 'grey' };
  }

  const thisMonth = today.startOf('month');
  const nextMonth = thisMonth.add(1, 'month');
  const prevMonth = thisMonth.subtract(1, 'month');

  if (!target.isBefore(nextMonth) && target.isBefore(nextMonth.add(1, 'month'))) return { label: 'Next Month', color: 'grey' };
  if (!target.isBefore(nextMonth.add(1, 'month')) && target.isBefore(nextMonth.add(2, 'month'))) return { label: '2 Months', color: 'grey' };
  if (!target.isBefore(prevMonth) && target.isBefore(thisMonth)) return { label: 'Last Month', color: 'grey' };
  if (!target.isBefore(prevMonth.subtract(1, 'month')) && target.isBefore(prevMonth)) return { label: '2 Months Ago', color: 'grey' };

  return { color: 'grey' };
}

// Group shows by their date
function groupShowsByDate(shows: ShowWithRawDate[], ascending: boolean = true): ShowGroup[] {
  const grouped: Map<string, ShowWithRawDate[]> = new Map();

  shows.forEach(show => {
    if (!show.rawDate) return;
    const dateKey = show.rawDate;
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(show);
  });

  // Sort dates
  const sortedDates = Array.from(grouped.keys()).sort((a, b) => {
    const diff = dayjs(a).valueOf() - dayjs(b).valueOf();
    return ascending ? diff : -diff;
  });

  return sortedDates.map(dateKey => {
    const date = dayjs(dateKey);
    return {
      label: date.format('ddd MMM D'),
      shows: grouped.get(dateKey)!.map(({ rawDate, ...show }) => show),
    };
  });
}

const AllShowsDrawer: React.FC<AllShowsDrawerProps> = ({
  open,
  onClose,
  shows = [],
  showGroups,
  allShows = [],
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming');

  const today = dayjs().startOf('day');

  const handleShowClick = (showId?: string) => {
    if (showId) {
      navigate(`/shows/${showId}`);
    }
  };

  const renderShows = (showList: ShowWithId[]) => (
    <div className="profile-shows__list">
      {showList.map((show, i) => (
        <ShowListing key={show.id || i} {...show} onClick={() => handleShowClick(show.id)} />
      ))}
    </div>
  );

  const renderGroupWithPill = (group: ShowGroup, i: number) => {
    const pill = group.pillLabel
      ? { label: group.pillLabel, color: group.pillColor || ('grey' as const) }
      : getDatePillInfo(group.label);
    return (
      <div key={i} className="profile-shows__group">
        <DateSubheader
          dateLabel={group.label}
          pillLabel={pill.label}
          pillColor={pill.color}
          showNavArrows={false}
          dateClickable={false}
        />
        {renderShows(group.shows)}
      </div>
    );
  };

  // Use allShows if provided, otherwise fall back to shows/showGroups
  const sourceShows: ShowWithRawDate[] = allShows.length > 0
    ? allShows
    : showGroups && showGroups.length > 0
      ? showGroups.flatMap(g => g.shows.map(s => ({ ...s, rawDate: undefined })))
      : shows.map(s => ({ ...s, rawDate: undefined }));

  // Filter into upcoming (today and future) and past
  const upcomingShows = sourceShows.filter(show =>
    show.rawDate && dayjs(show.rawDate).startOf('day').isSameOrAfter(today)
  );
  const pastShows = sourceShows.filter(show =>
    show.rawDate && dayjs(show.rawDate).startOf('day').isBefore(today)
  );

  // Group by date
  const upcomingGroups = groupShowsByDate(upcomingShows, true); // ascending (soonest first)
  const pastGroups = groupShowsByDate(pastShows, false); // descending (most recent first)

  const renderUpcomingContent = () => {
    if (upcomingGroups.length === 0) {
      return <div className="view-all-drawer__empty">No upcoming shows</div>;
    }
    return (
      <div className="profile-shows__grouped">
        {upcomingGroups.map(renderGroupWithPill)}
      </div>
    );
  };

  const renderPastContent = () => {
    if (pastGroups.length === 0) {
      return <div className="view-all-drawer__empty">No past shows</div>;
    }
    return (
      <div className="profile-shows__grouped">
        {pastGroups.map(renderGroupWithPill)}
      </div>
    );
  };

  const customTitle = (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      <div
        style={{
          width: '40px',
          height: '4px',
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '2px',
          margin: '0 auto 24px',
          flexShrink: 0,
        }}
      />
      All Shows
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: '8px',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <button onClick={onClose} aria-label="Close drawer" className="drawer-close-button">
          <XIcon size="m" tone="white" />
        </button>
      </div>
    </div>
  );

  return (
    <SpecDrawer
      title={customTitle}
      open={open}
      onClose={onClose}
      size="large"
      swipeable={false}
      closable={false}
      zIndex={1100}
    >
      <div className="view-all-drawer__content">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          centered
          items={[
            { key: 'upcoming', label: 'Upcoming', children: renderUpcomingContent() },
            { key: 'past', label: 'Past', children: renderPastContent() },
          ]}
        />
      </div>
    </SpecDrawer>
  );
};

export default AllShowsDrawer;
